import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, LayersControl } from 'react-leaflet';
import axios from 'axios';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet/dist/leaflet.css';
import 'leaflet-geosearch/dist/geosearch.css';
import './App.css';
import MapLegend from './components/MapLegend';
import AboutSection from './components/AboutSection';

function SearchField() {
  const map = useMap();
  useEffect(() => {
    const provider = new OpenStreetMapProvider();
    const searchControl = new GeoSearchControl({
      provider: provider,
      style: 'bar',
      showMarker: true,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: false,
      searchLabel: 'Enter address or landmark'
    });
    map.addControl(searchControl);
    return () => map.removeControl(searchControl);
  }, [map]);
  return null;
}


function App() {
  const [geoData, setGeoData] = useState(null);
  const [cityBoundsData, setCityBoundsData] = useState(null);
  const [selectedTract, setSelectedTract] = useState(null);
  const [reverseGeocode, setReverseGeocode] = useState(null);
  const [viewMode, setViewMode] = useState('business'); // 'business' or 'resident'
  const [selectedCounty, setSelectedCounty] = useState('All');
  const [availableCounties, setAvailableCounties] = useState([]);

  // Mapping of common FIPS to County Names for Georgia Expansion
  const fipsToCounty = {
    '077': 'Coweta',
    '113': 'Fayette',
    '121': 'Fulton',
    '089': 'DeKalb',
    '067': 'Cobb',
    '135': 'Gwinnett'
  };

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

    // Force static fetch for Vercel
    fetch('/map_data_v2.json')
      .then(res => res.json())
      .then(data => {
        setGeoData(data);

        // Extract unique counties for the dropdown
        if (data && data.features) {
          const counties = new Set();
          data.features.forEach(f => {
            if (f.properties.geoid) {
              counties.add(f.properties.geoid.substring(2, 5));
            }
          });
          setAvailableCounties(Array.from(counties).sort());
        }
      })
      .catch(error => console.error("Error fetching map data:", error));

    // Fetch City Boundaries
    // Wrapped in try/catch to fail silently if API is unreachable on Vercel
    try {
      axios.get(`${API_URL}/api/city-boundaries`)
        .then(response => {
          if (response.data) setCityBoundsData(response.data);
        })
        .catch(() => {
          // Silent fail for Vercel
        });
    } catch (e) {
      // Silent fail
    }
  }, []);

  // Utility to determine color based on metric
  const getColor = (value, mode) => {
    if (mode === 'resident') {
      // Resident View: High Saturation is Purple (Good)
      return value > 15 ? '#fde725' :
        value > 10 ? '#7ad151' :
          value > 5 ? '#22a884' :
            value > 3 ? '#2a788e' :
              value > 1 ? '#414487' :
                '#440154';
    } else {
      // Business View: High Opportunity Score is Yellow (Critical Gap)
      return value > 0.75 ? '#fde725' : // Top Quartile (Critical Gap)
        value > 0.50 ? '#5ec962' :      // Upper Mid
        value > 0.25 ? '#3b528b' :      // Lower Mid
        '#440154';                      // Bottom Quartile (Saturated / Well Served)
    }
  };

  const styleFeature = (feature) => {
    let val = 0;
    if (viewMode === 'resident') {
      val = feature.properties.tail_wag_index || 0;
    } else {
      val = feature.properties.opportunity_score || 0;
    }

    return {
      fillColor: getColor(val, viewMode),
      weight: 1.5,
      opacity: 0.8,
      color: 'white',
      fillOpacity: 0.7
    };
  };

  const cityBoundaryStyle = {
    color: '#000',
    weight: 2,
    dashArray: '5, 8',
    fillOpacity: 0
  };

  const onEachFeature = (feature, layer) => {
    const cityName = feature.properties.city_name || 'Unincorporated';
    const tooltipContent = `
      <strong>${cityName} Area</strong><br/>
      Dogs: ${Math.round(feature.properties.estimated_dog_pop || 0).toLocaleString()}<br/>
      Tail-Wag Index: ${(feature.properties.tail_wag_index || 0).toFixed(2)}<br/>
      Opportunity Score (Percentile): ${Math.round((feature.properties.opportunity_score || 0) * 100)}%
    `;
    layer.bindTooltip(tooltipContent, { sticky: true });

    layer.on({
      mouseover: (e) => {
        const layer = e.target;
        layer.setStyle({ weight: 5, color: '#666', fillOpacity: 0.9 });
        layer.bringToFront();
      },
      mouseout: (e) => {
        e.target.setStyle(styleFeature(feature));
      },
      click: (e) => {
        setSelectedTract(feature.properties);
        setReverseGeocode("Finding location...");

        const lat = e.latlng.lat;
        const lon = e.latlng.lng;

        axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=16`)
          .then(res => {
            if (res.data && res.data.display_name) {
              setReverseGeocode(res.data.display_name);
            } else {
              setReverseGeocode("Unknown Location");
            }
          })
          .catch(err => {
            console.error("Geocoding error", err);
            setReverseGeocode("Geocoding failed.");
          });
      }
    });

    layer.on('add', () => {
      layer.setStyle(styleFeature(feature));
    });
  };

  // Filter features by selected county
  let displayGeoData = geoData;
  if (geoData && selectedCounty !== 'All') {
    const filtered = geoData.features.filter(f => f.properties.geoid && f.properties.geoid.substring(2, 5) === selectedCounty);
    displayGeoData = { ...geoData, features: filtered };
  }

  return (
    <div className="App">
      <div className="map-container">
        {/* preferCanvas enables hardware acceleration for large multi-county vector loads */}
        <MapContainer center={[33.3968, -84.5563]} zoom={11} style={{ height: '100%', width: '100%' }} preferCanvas={true}>
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Base Map">
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
            </LayersControl.BaseLayer>
            {cityBoundsData && (
              <LayersControl.Overlay name="City Boundaries">
                <GeoJSON data={cityBoundsData} style={cityBoundaryStyle} />
              </LayersControl.Overlay>
            )}
            <LayersControl.Overlay checked name="Canine Demand Tracts">
              {displayGeoData && (
                <GeoJSON
                  key={`${selectedCounty}-${viewMode}`}
                  data={displayGeoData}
                  style={styleFeature}
                  onEachFeature={onEachFeature}
                />
              )}
            </LayersControl.Overlay>
          </LayersControl>
          <SearchField />
          <MapLegend viewMode={viewMode} />
        </MapContainer>
      </div>

      <div className="sidebar">
        <h2>Community Resource Map</h2>
        <p>Click a neighborhood or search an address.</p>

        <div style={{ padding: '10px 0', borderBottom: '1px solid #ccc', marginBottom: '10px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontWeight: 'bold', marginRight: '10px' }}>Lens:</label>
            <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} style={{ padding: '5px', borderRadius: '4px' }}>
              <option value="business">Business View (Opportunity Gaps)</option>
              <option value="resident">Resident View (Amenity Access)</option>
            </select>
          </div>
          <div>
            <label style={{ fontWeight: 'bold', marginRight: '10px' }}>County:</label>
            <select value={selectedCounty} onChange={(e) => setSelectedCounty(e.target.value)} style={{ padding: '5px', borderRadius: '4px' }}>
              <option value="All">All Counties</option>
              {availableCounties.map(fips => (
                <option key={fips} value={fips}>{fipsToCounty[fips] || `County ${fips}`}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedTract ? (
          <div className="details-card">
            <h3>{selectedTract.city_name || "Unincorporated"} Area</h3>
            <p style={{ fontSize: '0.8em', color: '#666' }}>📍 {reverseGeocode}</p>
            <hr />
            <p><strong>Estimated Dogs:</strong> {Math.round(selectedTract.estimated_dog_pop || 0).toLocaleString()}</p>
            <p><strong>Tail-Wag Index:</strong> {(selectedTract.tail_wag_index || 0).toFixed(3)}</p>
            <p><strong>Service Capacity Score:</strong> {Math.round((selectedTract.saturation_ratio || 0) * 100)} / 100</p>
            {(() => {
              const oppScore = selectedTract.opportunity_score || 0;
              const accessText = oppScore > 0.75 ? 'Critical Gap (High Opportunity)' : 
                                oppScore > 0.25 ? 'Moderate' : 
                                'Well Served (Low Opportunity)';
              return <p><strong>Relative Accessibility:</strong> {accessText}</p>;
            })()}
          </div>
        ) : (
          <div className="details-card empty">
            No active selection
          </div>
        )}

        <AboutSection />
      </div>
    </div>
  );
}

export default App;
